import React from "react";
import { connect } from "react-redux";
import { Tooltip } from "primereact/tooltip";
import { selectIsItemRemovalProcessing, selectIsRoomDesignProcessing, selectIsImageEditingProcessing, selectIsFloorPlanProcessing, selectIsPanoramaProcessing } from "../selectors";
import { MiniEditPlanDisabledTabs, PlanTypeExtension } from "../constants";

interface TabNavBarProps {
  activeTab: string;
  onTabChange: (tabName: string) => void;
  isItemRemovalProcessing: boolean;
  isRoomDesignProcessing: boolean;
  isImageEditingProcessing: boolean;
  isFloorPlanProcessing: boolean;
  isPanoramaProcessing: boolean;
  planType?: string;
}

const TabNavBar: React.FC<TabNavBarProps> = ({
  activeTab,
  onTabChange,
  isItemRemovalProcessing,
  isRoomDesignProcessing,
  isImageEditingProcessing,
  isFloorPlanProcessing,
  isPanoramaProcessing,
  planType,
}) => {
  // いずれかの機能が処理中かどうか
  const isAnyProcessing = isItemRemovalProcessing || isRoomDesignProcessing || isImageEditingProcessing || isFloorPlanProcessing || isPanoramaProcessing;

  // Mini(画像編集)プランかどうか
  const isMiniEditPlan = PlanTypeExtension.isMiniEdit(planType);

  // タブがMini(画像編集)プランで無効化されているかチェック
  const isDisabledByPlan = (tabId: string) => {
    return isMiniEditPlan && (MiniEditPlanDisabledTabs as readonly string[]).includes(tabId);
  };

  // タブボタンのクラス名を生成
  const getTabClassName = (tabId: string) => {
    const isActive = activeTab === tabId;
    const isDisabled = isDisabledByPlan(tabId);
    const isProcessingDisabled = isAnyProcessing && !isActive;

    let baseClass = "inline-block p-4 rounded-t-lg ";

    if (isDisabled) {
      // Mini(画像編集)プランで無効化されたタブ
      baseClass += "bg-gray-200 text-gray-400 cursor-not-allowed";
    } else if (isActive) {
      baseClass += "text-white bg-orange-500 shadow-md";
    } else {
      baseClass += "bg-gray-100 text-gray-600 hover:text-gray-700 hover:bg-gray-200";
    }

    if (isProcessingDisabled && !isDisabled) {
      baseClass += " opacity-50 cursor-not-allowed";
    }

    return baseClass;
  };

  // タブクリックハンドラ
  const handleTabClick = (tabId: string) => {
    if (isDisabledByPlan(tabId)) return;
    if (isAnyProcessing) return;
    onTabChange(tabId);
  };

  const DISABLED_TOOLTIP_MESSAGE = "\u2298 別プランの申し込みが必要です";

  const renderTabButton = (tabId: string, label: string) => {
    const disabled = (isAnyProcessing && activeTab !== tabId) || isDisabledByPlan(tabId);
    const disabledByPlan = isDisabledByPlan(tabId);

    return (
      <li className="me-2">
        <span
          className={disabledByPlan ? `tooltip-disabled-tab-${tabId}` : ""}
          data-pr-tooltip={disabledByPlan ? DISABLED_TOOLTIP_MESSAGE : undefined}
          data-pr-position="top"
          style={disabledByPlan ? { display: "inline-block" } : undefined}
        >
          <button
            className={getTabClassName(tabId)}
            type="button"
            onClick={() => handleTabClick(tabId)}
            disabled={disabled}
            style={disabledByPlan ? { pointerEvents: "none" } : undefined}
          >
            {label}
          </button>
        </span>
      </li>
    );
  };

  return (
    <div className="max-w-8xl w-full mx-auto py-3 px-4 sm:px-6 md:px-8">
      <Tooltip target=".tooltip-disabled-tab-roomDesign" className="orange-tooltip" />
      <Tooltip target=".tooltip-disabled-tab-itemRemoval" className="orange-tooltip" />
      <Tooltip target=".tooltip-disabled-tab-imageEditing" className="orange-tooltip" />
      <Tooltip target=".tooltip-disabled-tab-floorPlan" className="orange-tooltip" />
      <Tooltip target=".tooltip-disabled-tab-panorama" className="orange-tooltip" />
      <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200">
        {renderTabButton("roomDesign", "AIホームステージング")}
        {renderTabButton("itemRemoval", "AI家具消し")}
        {renderTabButton("imageEditing", "画像編集")}
        {renderTabButton("floorPlan", "マイソク編集")}
        {renderTabButton("panorama", "パノラマステージング")}
      </ul>
    </div>
  );
};

interface RootState {
  itemRemoval: {
    isLoading: boolean;
    selectedRegenImage: string | null;
    [key: string]: unknown;
  };
  roomDesign: {
    isLoading: boolean;
    selectedRegenImage: string | null;
    [key: string]: unknown;
  };
  imageEditing: {
    isProcessing: boolean;
    [key: string]: unknown;
  };
  floorPlanExtraction: {
    isLoading: boolean;
    [key: string]: unknown;
  };
  panorama: {
    isLoading: boolean;
    [key: string]: unknown;
  };
  currentUser: {
    planType?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const mapStateToProps = (state: RootState) => ({
  isItemRemovalProcessing: selectIsItemRemovalProcessing(state),
  isRoomDesignProcessing: selectIsRoomDesignProcessing(state),
  isImageEditingProcessing: selectIsImageEditingProcessing(state),
  isFloorPlanProcessing: selectIsFloorPlanProcessing(state),
  isPanoramaProcessing: selectIsPanoramaProcessing(state),
  planType: state.currentUser?.planType,
});

export default connect(mapStateToProps)(TabNavBar);
